import { useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  putAssetInMaintenance,
  completeAssetMaintenance,
  MaintenancePayload,
  CompleteMaintenancePayload
} from 'api/assetsAPI';
import { parseApiError } from 'utils/apiErrorParser';

interface MaintenanceAssetModalProps {
  show: boolean;
  handleClose: () => void;
  asset: {
    id: number;
    name: string;
    serial_number: string;
    status: string;
  } | null;
  onMaintenanceSuccess: () => void;
  mode: 'start' | 'complete';
}

const MaintenanceAssetModal = ({
  show,
  handleClose,
  asset,
  onMaintenanceSuccess,
  mode
}: MaintenanceAssetModalProps) => {
  const [maintenanceReason, setMaintenanceReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;

    setLoading(true);
    setError(null);

    try {
      if (mode === 'start') {
        const payload: MaintenancePayload = {};

        // Only add maintenance_reason if it's not empty
        if (maintenanceReason.trim()) {
          payload.maintenance_reason = maintenanceReason.trim();
        }

        await putAssetInMaintenance(asset.id, payload);
      } else {
        // Complete maintenance with payload
        const payload: CompleteMaintenancePayload = {
          completed_date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
        };

        // Add completion notes if provided
        if (maintenanceReason.trim()) {
          payload.completion_notes = maintenanceReason.trim();
        }

        await completeAssetMaintenance(asset.id, payload);
      }

      // Reset form
      setMaintenanceReason('');

      onMaintenanceSuccess();
      handleClose();
    } catch (err) {
      console.error('Failed to update maintenance status:', err);
      const errorMessage = parseApiError(
        err,
        'Failed to update maintenance status. Please try again.'
      );
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setMaintenanceReason('');
    setError(null);
    handleClose();
  };

  if (!asset) return null;

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">
          {mode === 'start'
            ? 'Put Device in Maintenance'
            : 'Complete Maintenance'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* Asset Information */}
          <div className="mb-3 p-3 bg-body-highlight rounded">
            <h6 className="mb-2 fs-7">Device Information</h6>
            <div className="row">
              <div className="col-6">
                <small className="text-body-tertiary d-block">Asset Name</small>
                <div className="fw-semibold fs-8">{asset.name}</div>
              </div>
              <div className="col-6">
                <small className="text-body-tertiary d-block">
                  Serial Number
                </small>
                <div className="fs-8">{asset.serial_number}</div>
              </div>
              <div className="col-12 mt-2">
                <small className="text-body-tertiary d-block">
                  Current Status
                </small>
                <div className="fs-8 text-capitalize">{asset.status}</div>
              </div>
            </div>
          </div>

          {/* Maintenance Reason - Only for start mode */}
          {mode === 'start' && (
            <Form.Group className="mb-3">
              <Form.Label>Maintenance Reason (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={maintenanceReason}
                onChange={e => setMaintenanceReason(e.target.value)}
                placeholder="e.g., Routine service, Hardware repair, Software update..."
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Provide context for why the device is being put in maintenance
              </Form.Text>
            </Form.Group>
          )}

          <Alert variant="info" className="mb-0">
            <i className="fas fa-info-circle me-2"></i>
            <small>
              {mode === 'start' ? (
                <>
                  Putting this device in maintenance will:
                  <ul className="mb-0 mt-2">
                    <li>Set status to "Maintenance"</li>
                    <li>Make the device unavailable for assignment</li>
                    <li>Record the maintenance event in history</li>
                  </ul>
                </>
              ) : (
                <>
                  Completing maintenance will:
                  <ul className="mb-0 mt-2">
                    <li>Set status to "Available"</li>
                    <li>Make the device available for assignment</li>
                    <li>Record the completion in history</li>
                  </ul>
                </>
              )}
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {mode === 'start' ? 'Setting...' : 'Completing...'}
              </>
            ) : mode === 'start' ? (
              'Put in Maintenance'
            ) : (
              'Complete Maintenance'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default MaintenanceAssetModal;
